import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Input } from 'antd';
import Avatar from '../Avatar';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

import { filterProjectSearchUsers, ProjectSearchUser } from './assignees';
import styles from './filters.module.css';

type Props = {
  children?: React.ReactNode;
  compact?: boolean;
  currentUser: ProjectSearchUser | null;
  listHeight: number;
  onSearchQueryChange?: (query: string) => void;
  onToggle: (userId: string) => void;
  rowHeight?: number;
  selectedUserIds: string[];
  users: ProjectSearchUser[];
};

const DROPDOWN_VIEWPORT_GUTTER = 16;

export const availableProjectUserDropdownHeight = (
  viewportHeight: number,
  dropdownTop: number,
  maxHeight = 600
) =>
  Math.max(
    0,
    Math.min(maxHeight, viewportHeight - dropdownTop - DROPDOWN_VIEWPORT_GUTTER)
  );

export const useProjectUserDropdownHeight = (
  open: boolean,
  maxHeight: number
) => {
  const [dropdownHeight, setDropdownHeight] = React.useState(maxHeight);
  const dropdownPanelRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    if (!open) return;

    const updateDropdownHeight = () => {
      const dropdownTop = dropdownPanelRef.current?.getBoundingClientRect().top;
      if (dropdownTop === undefined) return;

      const viewportHeight =
        window.visualViewport?.height || window.innerHeight;
      setDropdownHeight(
        availableProjectUserDropdownHeight(
          viewportHeight,
          dropdownTop,
          maxHeight
        )
      );
    };

    updateDropdownHeight();
    const animationFrame = window.requestAnimationFrame(updateDropdownHeight);
    window.addEventListener('resize', updateDropdownHeight);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateDropdownHeight);
    }

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', updateDropdownHeight);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateDropdownHeight
        );
      }
    };
  }, [maxHeight, open]);

  return { dropdownHeight, dropdownPanelRef };
};

const ProjectUserPicker: React.FC<Props> = ({
  children,
  compact = false,
  currentUser,
  listHeight,
  onSearchQueryChange,
  onToggle,
  rowHeight = 48,
  selectedUserIds,
  users
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const filteredUsers = filterProjectSearchUsers(users, searchQuery);
  const height = Math.min(listHeight, filteredUsers.length * rowHeight);
  const renderUser = ({ index, style }: ListChildComponentProps) => {
    const user = filteredUsers[index];

    return (
      <div style={style}>
        <label
          className={`${styles.assigneeRow} ${
            compact ? styles.compactUserRow : ''
          }`}
        >
          <Checkbox
            checked={selectedUserIds.includes(`${user.id}`)}
            onChange={() => onToggle(`${user.id}`)}
          />
          <Avatar member={user} size="small" className={styles.avatar} />
          <span
            className={`${styles.assigneeName} ${
              compact ? styles.compactUserName : ''
            }`}
          >
            {user.name}
          </span>
          {user.id === currentUser?.id && (
            <span className={styles.you}>You</span>
          )}
        </label>
      </div>
    );
  };

  return (
    <>
      <div
        className={`${styles.searchContainer} ${
          compact ? styles.compactSearchContainer : ''
        }`}
      >
        <Input
          className={`${styles.searchInput} ${
            compact ? styles.compactSearchInput : ''
          }`}
          onChange={event => {
            const query = event.target.value;
            setSearchQuery(query);
            if (onSearchQueryChange) onSearchQueryChange(query);
          }}
          placeholder="Search people..."
          prefix={<SearchOutlined />}
          value={searchQuery}
        />
      </div>
      {children}
      <div
        className={`${styles.peopleHeading} ${
          compact ? styles.compactPeopleHeading : ''
        }`}
      >
        People ({filteredUsers.length})
      </div>
      {filteredUsers.length ? (
        <FixedSizeList
          className={styles.assigneeList}
          height={height}
          itemCount={filteredUsers.length}
          itemSize={rowHeight}
          width="100%"
        >
          {renderUser}
        </FixedSizeList>
      ) : (
        <p className={styles.noPeople}>No people found</p>
      )}
    </>
  );
};

export default ProjectUserPicker;
